import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { getDashboardLiveTrendData, getDashboardLiveTrendLatestData, getIsDashboardLiveTrendFetching } from "../../model/slice/customDashboardSlice";
import PropTypes from "prop-types";
import { COLORS } from "../../constants/colors";
import { CHANNEL_TYPE } from "../../constants/channel/config";
import { DATA_GAP } from "../../constants/chart/chart";
// import { detectDevice, DEVICE_TYPE } from "../../constants/detectDevice";

export const LiveChart = (props) => {
  const { item, index, setChartRef, customLines } = props;
  const liveChartRef = useRef(null);
  const initialData = useSelector(getDashboardLiveTrendData);
  const latestData = useSelector(getDashboardLiveTrendLatestData);
  const isFetching = useSelector(getIsDashboardLiveTrendFetching);
  const [analogGraphData, setAnalogGraphData] = useState([]);
  const [digitalGraphData, setDigitalGraphData] = useState([]);
  const [channelTypes, setChannelTypes] = useState({ analog: [], digital: [] })
  const [series, setSeries] = useState();

  // const isMobile = [DEVICE_TYPE.PHONE, DEVICE_TYPE.TABLET].includes(detectDevice);

  const formatDataForDisplay = (baseData, chNos) => baseData.data.map((data) => {
    if(!data || data.length === 0) return [];
    const tempData = {};
    data.data.forEach((data) => {
      if(chNos.includes(data.chNo)) {
        Object.assign(tempData, {[data.chNo]: Number(data.value)})
      }
    });
    return({dateTime: Date.parse(data.dateTime), ...tempData });
  });

  function addData(chart, newData, chNos) {
    if(!newData?.data?.length) return;
    const displayData = formatDataForDisplay(newData, chNos);

    if(!series?.dataItems?.length) return;

    chart.series.values.forEach((series, i) => {
      const lastDataItem = series.dataItems[series.dataItems.length - 1];
      const lastValue = lastDataItem.get("valueY");
      const newValue = (displayData[0])[chNos[i]]
      const easing = am5.ease.linear;
      series.data.removeIndex(0);
      series.data.push(displayData[0])
      const newDataItem = series.dataItems[series.dataItems.length - 1];
      newDataItem.animate({
        key: "valueYWorking",
        to: newValue,
        from: lastValue,
        duration: 600,
        easing,
      });

      const animation = newDataItem.animate({
        key: "locationX",
        to: 0.5,
        from: -0.5,
        duration: 600
      });
      if (animation) {
        const xAxis = chart.xAxes.getIndex(0);
        const tooltip = xAxis.get("tooltip");
        if (tooltip && !tooltip.isHidden()) {
          animation.events.on("stopped", function () {
            xAxis.updateTooltip();
          })
        }
      }
    })
  }

  useEffect(() => {
    if(isFetching[item.id] === undefined || initialData[item.id] === undefined || isFetching[item.id] || initialData[item.id].length === 0) return;

    const anChnls = initialData[item.id].config.type.reduce((acc, val, idx) => {
      if (val === CHANNEL_TYPE.ANALOG) acc.push(idx);
      return acc;
    }, []);

    const analogChNos = item.chNos.filter((chNo, idx) => anChnls.includes(idx));
    if (analogChNos?.length > 0) {
      const analogData = formatDataForDisplay(initialData[item.id], analogChNos);
      setAnalogGraphData(analogData);
    }

    // const digitalChNos = item.chNos.filter((chNo, idx) => !anChnls.includes(idx));
    // if (digitalChNos?.length > 0) {
    //   const digitalData = formatDataForDisplay(initialData[item.id], digitalChNos);
    //   setDigitalGraphData(digitalData);
    // }

    setChannelTypes({
      analog: analogChNos,
      // digital: digitalChNos,
    });
  }, [isFetching[item.id]]);

  useLayoutEffect(() => {
    addData(liveChartRef.current, latestData[item.id], channelTypes.analog);
  }, [latestData[item.id]]);

  useLayoutEffect(() => {
    if (isFetching[item.id] || (analogGraphData?.length == 0 && digitalGraphData?.length == 0)) return;

    // *** License From 2023/05/22 ***
    am5.addLicense(process.env.AMCHARTS_LICENSE);
    // ******

    let analogRoot;
    if (analogGraphData?.length) {
      analogRoot = createAnalogChart();
    }

    // let digitalRoot;
    // if (digitalGraphData?.length) {
    //   digitalRoot = createDigitalChart();
    // }
    
    return () => {
      analogRoot?.dispose();
      // digitalRoot?.dispose();
    }
  }, [analogGraphData, digitalGraphData, customLines, isFetching[item.id]]);

  const createAnalogChart = () => {
    const root = am5.Root.new(`chartdiv-${index}`);
    root.setThemes([
      am5themes_Animated.new(root)
    ]);

    const chart = root.container.children.push(am5xy.XYChart.new(root, {
      focusable: true,
      panX: true,
      panY: true,
      wheelX: "panX",
      wheelY: "zoomX",
      maxTooltipDistance: -1,
    }));

    const xAxis = chart.xAxes.push(am5xy.DateAxis.new(root, {
      maxDeviation: 0.5,
      extraMin: -0.1,
      extraMax: 0.1,
      groupData: false,
      baseInterval: {
        timeUnit: 'second',
        count: 1
      },
      renderer: am5xy.AxisRendererX.new(root, {
        minorGridEnabled: true,
        minGridDistance: 60
      }),
      tooltip: am5.Tooltip.new(root, {})
    }));

    const xRenderer = xAxis.get("renderer");
    xRenderer.labels.template.setAll({
      fill: am5.color(COLORS.white),
      opacity: 0.2,
      fontSize: "12px",
      paddingTop: 14,
      centerX: am5.percent(20),
    });
    xRenderer.grid.template.setAll({
      stroke: am5.color(COLORS.white),
      strokeWidth: 2,
      strokeDasharray: [4, 2],
    });

    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererY.new(root, {})
    }));
    const yRenderer = yAxis.get("renderer");
    yRenderer.labels.template.setAll({
      fill: am5.color(COLORS.white),
      opacity: 0.2,
      fontSize: "14px",
    });
    yRenderer.grid.template.setAll({
      stroke: am5.color(COLORS.white),
      strokeWidth: 2,
      strokeDasharray: [4, 2],
    });

    const createSeries = (chNo) => {
      const data = initialData[item.id];
      const channelNumberIndex = data.config.chNo.findIndex((v) => v === chNo);
      const channelName = data.config.chName[channelNumberIndex];
      const unit = data.config.unit[channelNumberIndex];
      const color = customLines?.find(c => c.chNo == chNo)?.color;
      const chartSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: `${chNo}`,
          xAxis,
          yAxis,
          valueYField: String(chNo),
          valueXField: "dateTime",
          strokeWidth: item.thickness,
          interactionsEnabled: true,
          tooltip: am5.Tooltip.new(root, {
            labelText: `${channelName}: {valueY} ${unit}`,
            pointerOrientation: "vertical",
          }),
          connect: false,
          autoGapCount: DATA_GAP,
        })
      );

      if (color) {
        chartSeries.setAll({
          stroke: am5.color(color),
          fill: am5.color(color),
        });
      }

      chartSeries.data.setAll(analogGraphData);

      chartSeries.bullets.push(function () {
        return am5.Bullet.new(root, {
          locationX: undefined,
          sprite: am5.Circle.new(root, {
            radius: item.thickness ?? 2,
            fill: chartSeries.get("fill")
          }),
        });
      });

      setSeries(chartSeries);
    }

    channelTypes.analog.forEach((chNo) => createSeries(chNo));

    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
      xAxis,
    }));
    cursor.lineY.set("visible", false);

    chart.appear(1000, 100);

    liveChartRef.current = chart;
    setChartRef(liveChartRef);

    return root;
  }

  // const createDigitalChart = () => {
  //   const root = am5.Root.new(`digital-chartdiv-${index}`);
  //   root.setThemes([
  //     am5themes_Animated.new(root)
  //   ]);

  //   const touchOptions = isMobile ? {
  //     pinchZoomX: true,
  //     pinchZoomY: false,
  //     panX: true,
  //     panY: false,
  //   } : {
  //     panX: false,
  //     panY: false,
  //   };

  //   const chart = root.container.children.push(am5xy.XYChart.new(root, {
  //     focusable: true,
  //     wheelY: "zoomX",
  //       layout: root.verticalLayout,
  //       height: (channelTypes.digital.length * 35) + 100,
  //     ...touchOptions,
  //   }));

  //   const xAxis = chart.xAxes.push(am5xy.DateAxis.new(root, {
  //     maxDeviation: 0.5,
  //     extraMin: -0.1,
  //     extraMax: 0.1,
  //     groupData: false,
  //     baseInterval: {
  //       timeUnit: 'second',
  //       count: 1
  //     },
  //     renderer: am5xy.AxisRendererX.new(root, {
  //       minorGridEnabled: true,
  //       minGridDistance: 60
  //     }),
  //     tooltip: am5.Tooltip.new(root, {})
  //   }));

  //   const xRenderer = xAxis.get("renderer");
  //   xRenderer.labels.template.setAll({
  //     fill: am5.color(COLORS.white),
  //     opacity: 0.2,
  //     fontSize: "12px",
  //     paddingTop: 14,
  //     centerX: am5.percent(20),
  //   });
  //   xRenderer.grid.template.setAll({
  //     stroke: am5.color(COLORS.white),
  //     strokeWidth: 2,
  //     strokeDasharray: [4, 2],
  //   });

  //   const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
  //     renderer: am5xy.AxisRendererY.new(root, {})
  //   }));
  //   const yRenderer = yAxis.get("renderer");
  //   yRenderer.labels.template.setAll({
  //     fill: am5.color(COLORS.white),
  //     opacity: 0.2,
  //     fontSize: "14px",
  //   });
  //   yRenderer.grid.template.setAll({
  //     stroke: am5.color(COLORS.white),
  //     strokeWidth: 2,
  //     strokeDasharray: [4, 2],
  //   });

  //   const createSeries = (chNo) => {
  //     const data = initialData[item.id];
  //     const channelNumberIndex = data.config.chNo.findIndex((v) => v === chNo);
  //     const channelName = data.config.chName[channelNumberIndex];
  //     const unit = data.config.unit[channelNumberIndex];
  //     const color = customLines?.find(c => c.chNo == chNo)?.color;
  //     const chartSeries = chart.series.push(
  //       am5xy.LineSeries.new(root, {
  //         name: `${chNo}`,
  //         xAxis,
  //         yAxis,
  //         valueYField: String(chNo),
  //         valueXField: "dateTime",
  //         strokeWidth: item.thickness,
  //         interactionsEnabled: true,
  //         tooltip: am5.Tooltip.new(root, {
  //           labelText: `${channelName}: {valueY} ${unit}`,
  //           pointerOrientation: "vertical",
  //         })
  //       })
  //     );

  //     if (color) {
  //       chartSeries.setAll({
  //         stroke: am5.color(color),
  //         fill: am5.color(color),
  //       });
  //     }

  //     chartSeries.data.setAll(digitalGraphData);

  //     chartSeries.bullets.push(function () {
  //       return am5.Bullet.new(root, {
  //         locationX: undefined,
  //         sprite: am5.Circle.new(root, {
  //           radius: item.thickness,
  //           fill: chartSeries.get("fill")
  //         }),
  //       });
  //     });

  //     setSeries(chartSeries);
  //   }

  //   channelTypes.digital.forEach((chNo) => createSeries(chNo));

  //   const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
  //     xAxis,
  //   }));
  //   cursor.lineY.set("visible", false);

  //   chart.appear(1000, 100);

  //   liveChartRef.current = chart;
  //   setChartRef(liveChartRef);
  // }
return (
  <Box id={`chartdiv-${index}`} sx={{height: 'calc(100% - 30px)'}}>

  </Box>
)
  // return (
  //   <>
  //     <Box id={`analog-chartdiv-${item.id}-${index}`} sx={{ height: 'calc(100% - 50px)' }}>
  //     </Box>
  //     {/* {channelTypes.analog.length > 0 && <Box id={`analog-chartdiv-${item.id}`} sx={{ height: channelTypes.digital.length > 0 ? 'calc(70% - 50px)' : 'calc(100% - 50px)' }}>
  //     </Box>} */}
  //     {/* {channelTypes.digital.length > 0 && <Box id={`digital-chartdiv-${index}`} sx={{ height: channelTypes.analog.length > 0 ? 'calc(30%)' : 'calc(100%)', overflow: "auto" }}>
  //     </Box>} */}
  //   </>
  // );
}

LiveChart.propTypes = {
  item: PropTypes.object,
  chNos: PropTypes.array, 
  index: PropTypes.number,
  setChartRef: PropTypes.func,
  customLines: PropTypes.array,
};